import { styled, FormControl, Switch } from "@material-ui/core";
import { Star as StarIcon } from "@material-ui/icons";
import { brand } from "../../../theme";
import { FormLabel } from "../styles/shared";

export const AsteriskedField = styled(({
    selected,
    onChange,
    asterisksLeft,
    disabled,
    showDouble,
    doubleSelected,
    doubleDisabled,
    onDoubleChange,
    ...p
}) =>
    <FormControl
        margin="normal"
        fullWidth
        {...p}
    >
        <FormLabel>Asterisk</FormLabel>
        <div className={`card${selected ? ' card--on' : ''}`}>
            <div className="row">
                <div className="badge" aria-hidden>
                    <StarIcon />
                </div>
                <div className="copy">
                    <div className="title">Double or nothing</div>
                    <div className="subtitle">
                        +1 if you're right · −1 if you're wrong
                    </div>
                </div>
                <Switch
                    checked={selected}
                    onChange={onChange}
                    name="asterisked"
                    disabled={disabled}
                    inputProps={{ "data-testid": "asterisked" } as any}
                    color="primary"
                />
            </div>
            {selected && !doubleSelected && (
                <div className="info-chip">
                    <span aria-hidden>✨</span>
                    <span>+1 point if correct&nbsp;·&nbsp;−1 if wrong</span>
                </div>
            )}
            {showDouble && (
                <div className="double-section">
                    <div className={`double-row${doubleDisabled ? ' double-row--off' : ''}`}>
                        <div className="badge double-badge" aria-hidden>
                            <span>⭐</span>
                            <span>⭐</span>
                        </div>
                        <div className="copy">
                            <div className="title">Double Asterisk</div>
                            <div className="subtitle">
                                {doubleDisabled
                                    ? 'Not enough asterisks left'
                                    : 'Uses 2 asterisks · ±2 points'}
                            </div>
                        </div>
                        <Switch
                            checked={Boolean(doubleSelected)}
                            onChange={onDoubleChange}
                            name="doubleAsterisked"
                            disabled={doubleDisabled}
                            inputProps={{ "data-testid": "doubleAsterisked" } as any}
                            color="primary"
                        />
                    </div>
                    {doubleSelected && (
                        <div className="info-chip info-chip--strong">
                            <span aria-hidden>✨✨</span>
                            <span>+2 points if correct&nbsp;·&nbsp;−2 if wrong</span>
                        </div>
                    )}
                </div>
            )}
            <div className="footer">
                <span className="footer-label">Asterisks remaining</span>
                <span className="footer-count">{asterisksLeft} Left</span>
            </div>
        </div>
    </FormControl>
)(({ theme }) => ({
    marginTop: theme.spacing(3),
    "& .card": {
        background: brand.white,
        borderRadius: 14,
        padding: theme.spacing(2),
        boxShadow: "0 1px 4px rgba(26,18,5,0.06)",
        transition: "background .25s, box-shadow .25s",
    },
    "& .card--on": {
        background: `linear-gradient(135deg, ${brand.amber} 0%, ${brand.amberDeep} 100%)`,
        boxShadow: `0 10px 30px ${brand.amber}55`,
        color: brand.amberDark,
    },
    "& .row": {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    "& .badge": {
        width: 48,
        height: 48,
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: brand.amberLight,
        color: brand.amberDeep,
    },
    "& .card--on .badge": {
        background: "rgba(26,18,5,0.12)",
        color: brand.amberDark,
    },
    "& .badge .MuiSvgIcon-root": {
        fontSize: 26,
    },
    "& .copy": {
        flex: 1,
        minWidth: 0,
    },
    "& .title": {
        fontSize: 15,
        fontWeight: 800,
        color: brand.text,
    },
    "& .card--on .title": {
        color: brand.amberDark,
    },
    "& .subtitle": {
        fontSize: 12,
        color: brand.textSec,
        marginTop: 2,
    },
    "& .card--on .subtitle": {
        color: "rgba(26,18,5,0.75)",
    },
    "& .info-chip": {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.75),
        borderRadius: 10,
        background: brand.amberLight,
        border: `1px solid ${brand.amber}40`,
        fontSize: 13,
        fontWeight: 600,
        color: brand.amberDeep,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    "& .info-chip--strong": {
        background: `linear-gradient(135deg, ${brand.amber}22 0%, ${brand.amberDeep}22 100%)`,
        border: `1px solid ${brand.amber}60`,
        fontWeight: 700,
    },
    "& .card--on .info-chip": {
        background: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.35)",
        color: brand.amberDark,
    },
    "& .double-section": {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: `1px dashed ${brand.sep}`,
    },
    "& .card--on .double-section": {
        borderTopColor: "rgba(26,18,5,0.25)",
    },
    "& .double-row": {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    "& .double-row--off": {
        opacity: 0.5,
    },
    "& .double-badge": {
        fontSize: 16,
        gap: 1,
    },
    "& .footer": {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: `1px solid ${brand.sep}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: 12,
    },
    "& .card--on .footer": {
        borderTopColor: "rgba(26,18,5,0.15)",
    },
    "& .footer-label": {
        color: brand.textSec,
        fontWeight: 600,
    },
    "& .card--on .footer-label": {
        color: "rgba(26,18,5,0.75)",
    },
    "& .footer-count": {
        fontWeight: 800,
        color: brand.text,
    },
    "& .card--on .footer-count": {
        color: brand.amberDark,
    },
}));
